import React, { useState, useEffect } from 'react';
import { string, func, bool, array, oneOf } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import { Button, CountryCallingCodeSelect } from '@snapask/common-components';

import LineInput from '@dragonfruit/components/fields/LineInput';
import AnimatedCard from '@dragonfruit/components/authCard/AnimatedCard';
import CardContent from '@dragonfruit/components/authCard/CardContent';
import Link from '@dragonfruit/components/Link';
import LoginTypeSwitcher from '@dragonfruit/components/LoginTypeSwitcher';
import Disclaimer from '@dragonfruit/components/Disclaimer';
import { apiGetOauthNowTVLoginUrl } from '@dragonfruit/services/apis';
import {
  fetchRegionProvider,
  getAuthProviders,
  getStudentSignupAuthType,
} from '@dragonfruit/redux/signup';
import { login, facebookLogin } from '@dragonfruit/redux/auth';
import { openExternalUrl } from '@dragonfruit/redux/navigation';
import { useValidateField } from '@dragonfruit/hooks/fieldsValidation';

import {
  FACEBOOK_AUTH_TYPE,
  NOWTV_AUTH_TYPE,
  KAKAO_AUTH_TYPE,
} from '@dragonfruit/constants/authType';
import { USERNAME, PASSWORD } from '@dragonfruit/constants/autocompleteFields';
import { PHONE, EMAIL } from '@dragonfruit/constants/signupAuthType';

import styles from './LoginForm.scss';

function LoginForm(
  {
    transition,
    onSubmit,
    t,
    fetchRegionProvider,
    providerList,
    login,
    facebookLogin,
    openExternalUrl,
    studentSignupAuthType,
  },
  { region, urlLocale }
) {
  const [
    account,
    accountIsValidate,
    onAccountChange,
    onAccountValidate,
  ] = useValidateField();

  const [
    phoneNumber,
    phoneNumberIsValidate,
    onPhoneNumberChange,
    onPhoneNumberValidate,
  ] = useValidateField();

  const [
    password,
    passwordIsValidate,
    onPasswordChange,
    onPasswordValidate,
  ] = useValidateField();

  const [activeAuthType, setActiveAuthType] = useState(PHONE);
  const [countryCode, setCountryCode] = useState();
  const [submitDisabled, setSubmitDisabled] = useState(true);

  useEffect(() => {
    fetchRegionProvider();
    setActiveAuthType(studentSignupAuthType);
  }, [fetchRegionProvider, studentSignupAuthType]);

  useEffect(() => {
    const checkActiveAuthTypeFormIsValidate = authType => {
      switch (authType) {
        case EMAIL:
          return !!account && accountIsValidate;
        case PHONE:
          return !!phoneNumber && phoneNumberIsValidate;
      }
    };

    setSubmitDisabled(
      !checkActiveAuthTypeFormIsValidate(activeAuthType) || !password
    );
  }, [
    account,
    accountIsValidate,
    activeAuthType,
    password,
    phoneNumber,
    phoneNumberIsValidate,
  ]);

  const handleSubmit = event => {
    event.preventDefault();

    const formData = {
      account,
      password,
      countryCode: countryCode.value,
      phoneNumber: +phoneNumber,
      currentType: activeAuthType,
    };

    login(formData);
    if (typeof onSubmit === 'function') onSubmit();
  };

  const handleFacebookLogin = () => {
    facebookLogin();
    if (typeof onSubmit === 'function') onSubmit();
  };

  const handleNowTVLogin = async () => {
    const { authorizeUrl } = await apiGetOauthNowTVLoginUrl();
    openExternalUrl(authorizeUrl);
  };

  const handleChangeAuthType = authType => {
    switch (authType) {
      case FACEBOOK_AUTH_TYPE:
        return handleFacebookLogin();
      case NOWTV_AUTH_TYPE:
        return handleNowTVLogin();
      case KAKAO_AUTH_TYPE:
        // TODO: will add Kakao login type
        return;
      default:
        setActiveAuthType(authType);
    }
  };

  const authTypeFormList = {
    [EMAIL]: (
      <LineInput
        floatingLabelText={t('fieldLabel:fieldLabel.username-or-email')}
        onChange={onAccountChange}
        onBlur={onAccountValidate}
        autoComplete={USERNAME}
        errorText={!accountIsValidate && t('error:error.fieldEmpty')}
      />
    ),
    [PHONE]: (
      <div className={styles.phoneField}>
        <CountryCallingCodeSelect
          defaultRegion={region}
          value={countryCode}
          onChange={setCountryCode}
        />
        <LineInput
          isPhoneField
          floatingLabelText={t('fieldLabel:fieldLabel.phoneNumber')}
          onChange={onPhoneNumberChange}
          onBlur={onPhoneNumberValidate}
          errorText={!phoneNumberIsValidate && t('error:error.fieldEmpty')}
        />
      </div>
    ),
  };
  const renderAuthTypeForm = ([typeKey, typeForm]) => {
    return (
      <CardContent key={typeKey}>
        <div className={styles.form}>
          {typeForm}
          <LineInput
            type="password"
            floatingLabelText={t('fieldLabel:fieldLabel.password')}
            onChange={onPasswordChange}
            onBlur={onPasswordValidate}
            autoComplete={PASSWORD}
            errorText={!passwordIsValidate && t('error:error.fieldEmpty')}
          />
        </div>
        <div className={styles.forgetPassword}>
          <Link to={`/${urlLocale}/forgot-password`}>
            {t('login.forget-password')}
          </Link>
        </div>
        <Button
          onClick={handleSubmit}
          type="submit"
          text={t('studentSignup:studentSignup.links.login')}
          primary
          disabled={submitDisabled}
        />
        <Disclaimer type="login" />
      </CardContent>
    );
  };
  return (
    <div className={styles.root}>
      <LoginTypeSwitcher
        value={activeAuthType}
        onClickHandler={handleChangeAuthType}
        providerList={providerList}
      />
      <div className={styles.divider}>
        <div className={styles.text}>{t('login.or')}</div>
      </div>
      <form onSubmit={handleSubmit}>
        <AnimatedCard activeCard={activeAuthType} animate={transition}>
          {Object.entries(authTypeFormList).map(renderAuthTypeForm)}
        </AnimatedCard>
      </form>
    </div>
  );
}

LoginForm.propTypes = {
  facebookLogin: func.isRequired,
  fetchRegionProvider: func.isRequired,
  login: func.isRequired,
  openExternalUrl: func.isRequired,
  providerList: array.isRequired,
  studentSignupAuthType: oneOf([PHONE, EMAIL]).isRequired,
  t: func.isRequired,
  transition: bool,
  onSubmit: func,
};

LoginForm.defaultProps = {
  transition: true,
  onSubmit: undefined,
};

LoginForm.contextTypes = {
  region: string.isRequired,
  urlLocale: string.isRequired,
};

export default compose(
  translate(['login', 'fieldLabel', 'error', 'studentSignup']),
  connect(
    state => ({
      providerList: getAuthProviders(state),
      studentSignupAuthType: getStudentSignupAuthType(state),
    }),
    { fetchRegionProvider, login, facebookLogin, openExternalUrl }
  )
)(LoginForm);



// WEBPACK FOOTER //
// ./app/components/LoginForm/LoginForm.jsx